import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import axios from "axios";
import { Typography, Box } from "@material-ui/core/";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import config from "../../../app/config";
import FormBuilder from "../../FormBuilder";
import { updatePaymentInfo } from "../../../reduxSlices/applicationSlice";
import { formatDate } from "../../../utils/general";

const useStyles = makeStyles((theme) => ({
  paymentSec: {
    height: "90vh",
    width: "100vh",
    [theme.breakpoints.down("sm")]: {
      height: "100vh",
      minHeight: "100vh",
      width: "100%",
    },
    "& #moneris-checkout": {
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        minHeight: "85vh",
      },
    },
    "& #moneris-checkout iframe": {
      [theme.breakpoints.down("sm")]: {
        width: "100% !important",
        maxWidth: "100% !important",
        minHeight: "85vh",
      },
    },
  },
  paymentModal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: "fit-content",
    maxHeight: "90vh",
    overflowY: "auto",
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: theme.spacing(2),
    boxShadow: theme.shadows[5],
    [theme.breakpoints.up("md")]: {
      maxHeight: "calc(100vh - 64px)",
      overflowY: "auto",
    },
    [theme.breakpoints.down("sm")]: {
      width: "95vw",
      maxWidth: "95vw",
      height: "100vh",
      maxHeight: "100vh",
      overflowY: "auto",
    },
  },
  hiddenSegment: {
    display: "none",
  },
  classHeaderFont: {
    fontSize: "1.1rem",
    fontWeight: 800,
    flex: 1,
  },
  formDesign: {
    width: "98%",
    margin: "0 auto",
  },
}));

const MonerisPaymentPage = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  // Use forwardRef to receive ref from parent
  const classes = useStyles(props);
  const jsonData = props?.jsonData || [];

  const applicationId = props?.applicationId || null;
  const [open, setOpen] = useState(false);
  const [paymentState, setPaymentState] = useState(false);
  const [userDetailsFields, setUserDetailsFields] = useState({
    firstName: "",
    lastName: "",
    emailId: "",
    phone: "",
    orderId: "",
    price: window.localStorage.getItem("applicationfee"),
  });

  const textFieldChangeHandlerForJson = (event, key) => {
    const updatedData = {
      ...jsonData,
      [event.target.name]: event.target.value,
    };
    props.setJsonData(updatedData);
  };

  useEffect(() => {
    // Define event handler functions
    const myPageLoad = (data) => {
      console.log("Page Loaded:", JSON.parse(data));
    };

    const myCancelTransaction = (data) => {
      console.log("Transaction Cancelled:", JSON.parse(data));
      const updatedData = {
        ...jsonData,
        paymentdone: false,
        ticket: "PaymentNotdone",
      };
      props.setJsonData(updatedData);

      setOpen(false);
    };

    const myErrorEvent = (data) => {
      console.error("Error Event:", JSON.parse(data));
    };
    const checkPaymentResponse = async (ticket) => {
      try {
        const response = await axios.post(
          `${config.HOST_NAME}/paymentResponse`,
          {
            headers: {
              "Content-Type": "application/json",
            },
            data: { action: "receipt", ticket: ticket },
          },
        );

        // Return the required payment response code
        return response?.data?.response?.receipt?.cc;
      } catch (error) {
        console.error("Payment initiation error:", error);
        return null; // Return null or handle the error accordingly
      }
    };

    const myPaymentReceipt = async (data) => {
      try {
        const paymentReceipt = await checkPaymentResponse(
          JSON.parse(data).ticket,
        );
        console.log(paymentReceipt, "paymentReceipt");
        console.log(data, "data");
        if (
          paymentReceipt?.response_code.toString() === "421" ||
          paymentReceipt?.response_code.toString() === "499"
        ) {
          const updatedData = {
            ...jsonData,
            paymentdone: false,
            ticket: "PaymentNotdone",
          };
          props.setJsonData(updatedData);
          setOpen(false);
          return;
        }

        if (paymentReceipt?.response_code.toString() < "029") {
          const updatedData = {
            ...jsonData,
            paymentdone: true,
            ticket: JSON.parse(data).ticket,
          };

          // Uncomment these lines if needed for further actions
          props.setJsonData(updatedData);
          const paymentInfo = {
            ses_applicationfeepaiddate: formatDate(new Date().toISOString()),
            usb_paymentauthorization: JSON.parse(data).ticket,
            ses_applicationfeepaid: true,
            usb_orderid: paymentReceipt.order_no,
            usb_applicationfeeamount: Number(paymentReceipt.amount),
            usb_approvalcode: paymentReceipt.approval_code,
            usb_responsecode: paymentReceipt.response_code,
          };

          dispatch(updatePaymentInfo(paymentInfo, applicationId));
          setOpen(false);
        } else {
          const updatedData = {
            ...jsonData,
            paymentdone: false,
            ticket: "PaymentNotdone",
          };
          props.setJsonData(updatedData);
          console.error("Payment not received or invalid.");
          setOpen(false);
        }
      } catch (error) {
        console.error("Error in myPaymentReceipt:", error);
      }
    };

    const myPaymentComplete = (data) => {
      console.log("Payment Complete:", JSON.parse(data));
      setOpen(false);
    };

    // Initialize and set the callbacks for myCheckout if payment state is true
    if (paymentState) {
      const myCheckout = new monerisCheckout(); // eslint-disable-line
      myCheckout.setMode("qa");
      myCheckout.setCheckoutDiv("moneris-checkout");

      // Set callback functions
      myCheckout.setCallback("page_loaded", myPageLoad);
      myCheckout.setCallback("cancel_transaction", myCancelTransaction);
      myCheckout.setCallback("error_event", myErrorEvent);
      myCheckout.setCallback("payment_receipt", myPaymentReceipt);
      myCheckout.setCallback("payment_complete", myPaymentComplete);
    }
  }, [paymentState]);

  // Function to handle the payment process
  const handleMakePayment = () => {
    axios
      .post(`${config.HOST_NAME}/initiatePayment`, {
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify(userDetailsFields),
      })
      .then((response) => {
        setOpen(true);
        setTimeout(() => {
          // Initialize Moneris Checkout only after ensuring the element exists
          const myCheckout = new monerisCheckout(); // eslint-disable-line
          myCheckout.setMode("qa");
          myCheckout.setCheckoutDiv("moneris-checkout");
          myCheckout.startCheckout([response.data.response.ticket]);

          // Set the callbacks after initialization
          myCheckout.setCallback("page_loaded", window.myPageLoad);
          setPaymentState(true);
        }, 1000);
      })
      .catch((error) => {
        console.error("Payment initiation error:", error);
      });
  };

  // Expose the handleMakePayment function to the parent component
  useImperativeHandle(ref, () => ({
    handleMakePayment,
  }));

  const handleClose = () => setOpen(false);

  return (
    <div className="wrapperInside">
      <div className="card w-100">
        <div className="card-body">
          <div className={`userDetails ${paymentState ? "hiddenSegment" : ""}`}>
            {jsonData?.ticket !== "PaymentNotdone" && (
              <div className={classes.formDesign}>
                <FormBuilder
                  formDetails={props.formData}
                  value={jsonData}
                  changed={textFieldChangeHandlerForJson}
                />
              </div>
            )}
          </div>

          <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box className={classes.paymentModal}>           
              <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                <div
                  className={`${classes.paymentSec} ${
                    !paymentState ? classes.hiddenSegment : ""
                  }`}
                >
                    <div id="moneris-checkout"></div>
                  <input type="hidden" name="currency_code" value="CAD"></input>
                  <input
                    type="hidden"
                    name="payment_method"
                    value="VISA,MASTERCARD"
                  ></input>
                </div>
              </Typography>
            </Box>
          </Modal>
        </div>
      </div>
    </div>
  );
});

export default MonerisPaymentPage;
